import { DateTime } from 'luxon';
import { ceil, floor } from 'lodash';
import { max } from 'mathjs';

import { maxPatientAge } from 'constants/constants';

/**
 * Gets the current date in YYYY-MM-DD format
 * Mainly used internally as date display is locale-based
 */
export const getCurrentDate = () => {
  const today = new Date();
  let dd: any = today.getDate();
  let mm: any = today.getMonth() + 1; // January is 0
  const yyyy = today.getFullYear();

  if (dd < 10) dd = '0' + dd;
  if (mm < 10) mm = '0' + mm;

  return yyyy + '-' + mm + '-' + dd;
};

/**
 * Gets the latest possible birthday for a patient given a minimum age
 * @param {Number} age the minimum age for patients (18 usually)
 * @returns {Date} the latest available birthday
 */
export const getMaxBirthday = (age: number = maxPatientAge) => {
  const currentDate = getCurrentDate();
  const year = currentDate.slice(0, 4);
  const maxYear = (Number(year) - age).toString();
  const maxBirthday = currentDate.replace(year, maxYear);

  return maxBirthday;
};

/**
 * Gets a segment of a date
 * @param {Date} date the date
 * @param {String} period the segment you want, either 'year', 'month' or 'day'
 * @returns {Number} the year, month or day of the date
 */
export const parseDate = (date: string, period: string) => {
  switch (period) {
    case 'year':
      return Number(date.slice(0, 4));
    case 'month':
      return Number(date.slice(5, 7));
    case 'day':
      return Number(date.slice(8));
    default:
      return 0;
  }
};

/**
 * Formatting Date Strings to ISO format
 * @returns {String} Formatted ISO date
 */
export const formatToISO = (date: any) => {
  const formatDate = new Date(date);
  return formatDate.toISOString();
};

/**
 * find the start and end date of a single date set
 * @param  {any} setDate date set by the date picker
 * @param  {string} type Range selected from the date (monthly, yearly, daily)
 * @param  {Boolean} end set an end date of specific date
 * @return {string} Date that is the start and/end of the date set
 */
export const findDate = (setDate: any, type: string, end = false) => {
  const newDate = new Date(setDate);
  switch (type) {
    case '2': {
      const monthEnd = end ? 0 : 1;
      const increment = end ? 1 : 0;
      newDate.setMonth(newDate.getMonth() + increment, monthEnd);
      break;
    }
    case '3': {
      const mm = end ? 11 : 0;
      const dd = end ? 31 : 1;
      newDate.setFullYear(newDate.getFullYear(), mm, dd);
      break;
    }
  }

  return newDate;
};

const getDateTimeFromBillingStartDate = (billingStartDate: string, timezone: string) => {
  return DateTime.fromFormat(billingStartDate + ' 00:00:00', 'yyyy-MM-dd HH:mm:ss', {
    zone: timezone
  });
};

const getPeriodsCountFromBillingStartDate = (billingStartDate: string, timezone: string) => {
  const thirtyDaysInMilliseconds = 2592000000;
  return max(
    0,
    floor(
      (Date.now() - getDateTimeFromBillingStartDate(billingStartDate, timezone).toMillis()) /
        thirtyDaysInMilliseconds
    )
  );
};

export enum DefaultBillingDate {
  CALENDAR_DATE = 'calendarDate',
  REFERRED_DATE = 'referredDate',
  FIRST_READING_TRANSMITTED = 'firstReadingTransmitted'
}

export const getBillingPeriodStart = (
  billingStartDate: string | undefined,
  defaultBillingDate: string | undefined,
  timezone: string,
  locale: string
) => {
  if (!billingStartDate) return '-';
  if (defaultBillingDate !== DefaultBillingDate.CALENDAR_DATE) {
    return getDateTimeFromBillingStartDate(billingStartDate, timezone)
      .plus({ days: 30 * getPeriodsCountFromBillingStartDate(billingStartDate, timezone) })
      .setLocale(locale)
      .toLocaleString(DateTime.DATE_SHORT);
  } else {
    return DateTime.now()
      .setZone(timezone)
      .setLocale(locale)
      .startOf('month')
      .toLocaleString(DateTime.DATE_SHORT);
  }
};

export const getBillingPeriodEnd = (
  billingStartDate: string | undefined,
  defaultBillingDate: string | undefined,
  timezone: string,
  locale: string
) => {
  if (!billingStartDate) return '-';
  if (defaultBillingDate !== DefaultBillingDate.CALENDAR_DATE) {
    return getDateTimeFromBillingStartDate(billingStartDate, timezone)
      .setZone(timezone)
      .setLocale(locale)
      .plus({ days: 30 * (getPeriodsCountFromBillingStartDate(billingStartDate, timezone) + 1) })
      .minus({ minutes: 1 })
      .toLocaleString(DateTime.DATE_SHORT);
  } else {
    return DateTime.now()
      .setZone(timezone)
      .setLocale(locale)
      .endOf('month')
      .toLocaleString(DateTime.DATE_SHORT);
  }
};

export const getDayInPeriod = (
  billingStartDate: string | undefined,
  defaultBillingDate: string | undefined,
  timezone: string
) => {
  if (!billingStartDate) return '-';
  if (defaultBillingDate !== DefaultBillingDate.CALENDAR_DATE) {
    return ceil(
      DateTime.now()
        .setZone(timezone)
        .diff(
          getDateTimeFromBillingStartDate(billingStartDate, timezone)
            .setZone(timezone)
            .plus({ days: 30 * getPeriodsCountFromBillingStartDate(billingStartDate, timezone) }),
          ['days']
        ).days
    );
  } else {
    return DateTime.now().setZone(timezone).day;
  }
};

export const formatTimeStringForRecurrence = (time?: string) => {
  if (!time) return { hour: 0, minute: 0 };
  const splitTime = time.split(':');
  return { hour: parseInt(splitTime[0]), minute: parseInt(splitTime[1]), second: 0 };
};
